﻿import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import Uppy, { UppyFile } from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import XHRUpload from '@uppy/xhr-upload';
import Compressor, { CompressorOptions } from '@uppy/compressor';
import { fetchRequest } from '@tk/utilities/tk.fetch';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore the type definition cannot be found
import German from '@uppy/locales/lib/de_DE';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore the type definition cannot be found
import French from '@uppy/locales/lib/fr_FR';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore the type definition cannot be found
import Italian from '@uppy/locales/lib/it_IT';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore the type definition cannot be found
import English from '@uppy/locales/lib/en_US';

// #region Interfaces & Types
interface ResponseData {
    path?: string;
    name: string;
    fileId: string;
    success: boolean;
}

interface OtherCompressorOptions {
    mimeType: string;
}

type Language = German | French | English | Italian;
type ExpandCompressorOptions = CompressorOptions & OtherCompressorOptions;
// #endregion

/**
 * TKFileUploadArea
 * A custom element wrapper for the Uppy file upload ecosystem.
 */
export default class TKFileUploadArea extends TKCustomElementFactory {
    // #region Properties
    /** The Uppy instance */
    uppy?: Uppy;
    /** Base API URL for the initial upload */
    api?: string;
    /** API URL to move the file after successful upload */
    moveAPI?: string;
    /** Usage identifier (e.g., 'avatar', 'document') */
    usage?: string;
    /** Target file path on the server */
    filePath?: string;
    /** Whether to skip the image compression step */
    disableCompressor: boolean;
    /** Desired output format for compressed images (default: image/webp) */
    outputMimeType: string;
    /** Maximum number of files allowed in one session */
    maxNumberOfFiles: number | null;
    /** Maximum allowed size for a single file in bytes */
    maxFileSize: number | null;
    /** List of allowed MIME types or extensions */
    allowedFileTypes: string[];
    /** Numeric locale ID (1: DE, 2: FR, 3: EN, 4: IT) */
    locale: number;
    /** Uppy locale object based on the selected language */
    language: Language;
    /** Optional prefix for generated filenames */
    filePrefix?: string;
    /** If true, the original filename is preserved instead of generating a UUID */
    disableUniqueFileNames: boolean;
    /** If true, the 'path' parameter is excluded from API calls */
    disablePath: boolean;
    /** Indicates if the component is running inside an iframe (e.g., TinyMCE dialog) */
    isWithinIFrame: boolean = false;
    /** If true, the internal Uppy "Upload" button is hidden to allow external triggering via hooks */
    manualTrigger: boolean;
    // #endregion

    // #region Constructor & Lifecycle
    /**
     * Initializes component properties by reading data-attributes from the DOM.
     */
    constructor() {
        super();
        this.api = this.getAttribute('data-tk-api') || undefined;
        this.moveAPI = this.getAttribute('data-tk-move-api') || undefined;
        this.usage = this.getAttribute('data-tk-usage') || undefined;
        this.filePath = this.getAttribute('data-tk-file-path') || undefined;
        this.disableCompressor = this.hasAttribute('data-tk-disable-compressor');
        this.outputMimeType = this.getAttribute('data-tk-output-mime-type') || 'image/webp';
        this.maxNumberOfFiles = Number(this.getAttribute('data-tk-max-number-of-files')) || null;
        this.maxFileSize = Number(this.getAttribute('data-tk-max-file-size')) || null;
        this.allowedFileTypes = (this.getAttribute('data-tk-allowed-file-types') || 'image/*').split(',');
        this.locale = Number(this.getAttribute('data-tk-locale')) || 1;
        this.disableUniqueFileNames = this.hasAttribute('data-tk-disable-unique-file-names');
        this.disablePath = this.hasAttribute('data-tk-disable-path');
        this.manualTrigger = this.hasAttribute('data-tk-manual-trigger');

        const getLanguage: Record<number, Language> = {
            1: German,
            2: French,
            3: English,
            4: Italian,
        };
        this.language = getLanguage[this.locale];
        this.filePrefix = this.getAttribute('data-tk-file-prefix') || undefined;
        this.isWithinIFrame = this.hasAttribute('data-tk-is-within-iframe');
    }

    /**
     * Lifecycle method: Validates required attributes and starts initialization.
     * @throws {Error} If mandatory API attributes are missing.
     */
    connectedCallback(): void {
        if (!this.api || !this.moveAPI || !this.usage || (!this.disablePath && !this.filePath))
            throw new Error('FileUploadArea: Attributes are missing!');
        this.init();
    }
    // #endregion

    // #region Lifecycle Hooks (Protected)
    /**
     * Hook: Invoked immediately before the upload process begins.
     * Override this method in a subclass to add custom validation or confirmation logic.
     * * @param files - The current list of files in the Uppy queue.
     * @returns A promise resolving to true to proceed, or false to abort the upload.
     */
    protected async onBeforeUpload(
        files: UppyFile<Record<string, unknown>, Record<string, unknown>>[],
    ): Promise<boolean> {
        console.log('Hook: Before Upload', files);
        return true;
    }

    /**
     * Hook: Invoked after the upload and the subsequent "moveFile" API call are successful.
     * * @param responseData - The JSON data returned from the move API.
     */
    protected onAfterUploadSuccess(responseData: ResponseData): void {
        console.log('Hook: After Upload Success', responseData);
    }

    /**
     * Hook: Invoked when an error occurs during any stage of the upload process.
     * * @param error - The error object or message.
     */
    protected onUploadError(error: { name: string; message: string; details?: string | undefined }): void {
        console.error('TKFileUploadArea Error:', error);
    }
    // #endregion

    // #region Public API
    /**
     * Manually triggers the upload process.
     * Useful when `data-tk-manual-trigger` is used and the internal button is hidden.
     * * @returns A promise that resolves when the upload attempt is finished.
     */
    public async triggerUpload() {
        if (!this.uppy) return;

        const files = this.uppy.getFiles();
        const shouldStart = await this.onBeforeUpload(files);

        if (shouldStart && files.length > 0) {
            return this.uppy.upload();
        }
    }
    // #endregion

    // #region Internal Logic (Private)
    /**
     * Configures Uppy, Dashboard, XHRUpload, and Compressor.
     */
    protected init() {
        const endpoint = `${this.api}${this.usage}`;
        this.uppy = new Uppy();
        this.uppy.setOptions({
            restrictions: {
                maxFileSize: this.maxFileSize,
                maxNumberOfFiles: this.maxNumberOfFiles,
                allowedFileTypes: this.allowedFileTypes,
            },
            locale: this.language,
            autoProceed: false,
        });

        this.uppy.use(Dashboard, {
            target: this,
            inline: true,
            width: '100%',
            hideUploadButton: this.manualTrigger,
            proudlyDisplayPoweredByUppy: false,
        });

        this.uppy.use(XHRUpload, {
            endpoint,
            formData: true,
            limit: 1,
            allowedMetaFields: [],
            getResponseData: (xhr: XMLHttpRequest) => this.getResponseData(xhr),
        });

        if (!this.disableCompressor) {
            this.uppy.use(Compressor, {
                mimeType: this.outputMimeType,
            } as ExpandCompressorOptions);
        }

        this.uppy.on('file-added', (file) => {
            const modifiedFileName = this.getFileName(file);
            this.uppy?.setFileMeta(file.id, { name: modifiedFileName });
            this.uppy?.setFileState(file.id, { name: modifiedFileName });
        });

        this.uppy.on('error', (error) => this.onUploadError(error));
    }

    /**
     * Handles the XHR response and triggers the secondary move API call.
     * * @param xhr - The finished XMLHttpRequest object.
     * @returns An object containing the final file URL for Uppy.
     */
    protected async getResponseData(xhr: XMLHttpRequest) {
        if (xhr.status === 200) {
            try {
                const response = await this.moveFile();
                const { success } = response.dataAsJson;

                if (!success) {
                    const error = { name: 'CANCEL', message: 'FAILED!' };
                    this.uppy?.emit('error', error);
                    this.uppy?.emit('cancel-all');
                    return { url: '' };
                }

                const path = this.disablePath ? '' : response.dataAsJson.path || '';

                this.onAfterUploadSuccess(response.dataAsJson);

                if (this.isWithinIFrame) {
                    window.parent.postMessage(
                        {
                            mceAction: 'close',
                            content: path,
                            size: { width: 0, height: 0 },
                        },
                        '*',
                    );
                }
                return { url: path };
            } catch (err) {
                const error = {
                    name: 'UPLOAD_ERROR',
                    message: err instanceof Error ? err.message : 'Unknown error occurred',
                };
                this.onUploadError(error);
                return { url: '' };
            }
        }
        return { url: '' };
    }

    /**
     * Executes a fetch request to notify the server that the file should be moved
     * from temporary storage to its final destination.
     */
    protected async moveFile() {
        const data: { usage?: string; path?: string } = {
            usage: this.usage,
        };

        if (!this.disablePath) {
            data.path = this.filePath;
        }

        return new Promise<TKResponse<ResponseData>>((resolve) => {
            fetchRequest<ResponseData>({
                requestURL: this.moveAPI!,
                resolveHandler: (success) => resolve(success),
                payload: data,
            });
        });
    }

    /**
     * Generates the filename based on component configuration (UUID vs Original vs Prefix).
     * * @param file - The Uppy file object.
     * @returns The formatted filename string.
     */
    protected getFileName(file: UppyFile<Record<string, unknown>, Record<string, unknown>>) {
        const id = crypto.randomUUID();
        if (this.disableUniqueFileNames && this.filePrefix) {
            return `${this.filePrefix}_${file.name}`;
        }
        if (!this.disableUniqueFileNames && this.filePrefix) {
            return `${this.filePrefix}_${id}.${file.extension}`;
        }
        if (!this.disableUniqueFileNames && !this.filePrefix) {
            return `${id}.${file.extension}`;
        }
        return file.name;
    }
    // #endregion
}
